import { useIntersection } from '@mantine/hooks'
import { Suspense, useContext, useEffect, useRef, useState } from 'react'
import {
  CommentComposer,
  CommentList,
  CommentProvider,
} from 'src/components/ItemComment'
import { useCommentsSectionRef } from 'src/components/ItemComment/commentsScroll'
import {
  CommentContext,
  ensureContext,
} from 'src/components/ItemComment/context'
import Text from 'src/components/ui/Text'
import { EmailVerifyBanner } from 'src/features/User/ForceVerifyEmail/EmailVerifyBanner'
import { RefType } from 'src/services/apollo/generated/graphql'
import { track } from 'src/utils/tracker'
import { ArtworkDetailContext } from '../common'

export function ArtworkDetailCommentAreaRedesign() {
  const { artwork, userIsAuthor, isSinglePage } =
    useContext(ArtworkDetailContext)
  const commentsRef = useCommentsSectionRef()
  const [autoLoadEnabled, setAutoLoadEnabled] = useState(false)

  if (!artwork) return null

  // In the modal: overlay scrolls with pt-16px (≥900px) or <main> with
  // py-24px (<900px); negative top compensates so the composer sits flush.
  const stickyTopClass = isSinglePage
    ? 'top-[--app-header-height]'
    : 'top-[-24px] mmd:top-[-16px]'

  return (
    <section
      ref={commentsRef}
      className="w-full max-msm:px-2 scroll-mt-[--app-header-height]"
    >
      <Suspense>
        <CommentProvider
          topicId={artwork.id}
          refType={RefType.Artwork}
          creatorId={artwork.author?.id}
          totalCount={artwork.commentCount ?? undefined}
          notifyOnNetworkStatusChange
          initLoadSize={3}
          canDeleteMessage={() => userIsAuthor}
          onLoadMore={() => {
            if (!autoLoadEnabled) track('show_more_comments')
            setAutoLoadEnabled(true)
          }}
        >
          <div
            className={`sticky ${stickyTopClass} -mx-2 px-2 pb-3 pt-4 -mt-4 bg-background border-b border-black/10 dark:border-white/10 z-10`}
          >
            <h3 className="font-medium text-base flex items-baseline mb-3">
              <Text i18nKey="common:comments" defaults="Comments" />
              {(artwork.commentCount ?? 0) > 0 && (
                <span className="text-neutral-500 pl-2 text-xs">
                  {artwork.commentCount}
                </span>
              )}
            </h3>
            <EmailVerifyBanner className="mb-3" />
            <CommentComposer />
          </div>
          <div className="mt-4">
            <CommentList />
          </div>
          {!isSinglePage && autoLoadEnabled && <AutoLoadSentinel />}
        </CommentProvider>
      </Suspense>
    </section>
  )
}

/** Fires loadMore at most once per scroll-arrival at the bottom — the
 * sentinel must leave the viewport and re-enter before firing again, so a
 * single scroll doesn't chain-load every remaining page. */
function AutoLoadSentinel() {
  const { loadMore, havingMore, loading } =
    useContext(CommentContext) ?? ensureContext()
  const { ref, entry } = useIntersection({ rootMargin: '200px' })
  const intersecting = entry?.isIntersecting ?? false
  const armedRef = useRef(true)

  useEffect(() => {
    if (!intersecting) {
      armedRef.current = true
      return
    }
    if (!armedRef.current || !havingMore || loading) return
    armedRef.current = false
    loadMore()
  }, [intersecting, havingMore, loading, loadMore])

  return <div ref={ref} aria-hidden className="h-px" />
}
